import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Modal,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import "./PoolMember.css";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import EditMemberModal from "./EditMemberModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MemberMeasurements from "../GymMembers/MemberMeasurements";
import { poolMembersService } from '../../services';

const PoolMembers = ({ setTodayMembersCount }) => {
  //props for count
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedMembers, setHasLoadedMembers] = useState(false);
  const [feesFilter, setFeesFilter] = useState("all");
  const [anchorEl, setAnchorEl] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const pendingToastSignatureRef = useRef("");

  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);

  const [measurementsModalOpen, setMeasurementsModalOpen] = useState(false);
  const [selectedMemberMeasurements, setSelectedMemberMeasurements] =
    useState(null);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = (filterValue) => {
    if (filterValue) {
      setFeesFilter(filterValue);
      setPage(0);
    }
    setAnchorEl(null);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    const handleRefresh = () => {
      setPage(0);
      setReloadKey((currentValue) => currentValue + 1);
    };

    window.addEventListener('pool-members:refresh', handleRefresh);

    return () => {
      window.removeEventListener('pool-members:refresh', handleRefresh);
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();

    const fetchMembers = async () => {
      setIsLoading(true);

      try {
        const response = await poolMembersService.getPoolMembersPage({
          page: page + 1,
          pageSize: rowsPerPage,
          search: debouncedSearchTerm,
          feesStatus: feesFilter,
          signal: controller.signal,
        });

        if (!isActive) {
          return;
        }

        const nextMembers = response.members || [];
        const nextTotalCount = response.pagination?.totalCount || 0;
        const summary = response.summary || {};

        setMembers(nextMembers);
        setTotalCount(nextTotalCount);

        if (typeof setTodayMembersCount === 'function' && typeof summary.todayCount === 'number') {
          setTodayMembersCount(summary.todayCount);
        }

        if (!debouncedSearchTerm && feesFilter === 'all') {
          const pendingCount = summary.pendingCount || 0;
          const pendingMembers = summary.pendingMembers || [];
          const signature = `${pendingCount}:${pendingMembers.join('|')}`;

          if (pendingCount === 0) {
            pendingToastSignatureRef.current = '';
          }

          if (pendingCount > 0 && pendingToastSignatureRef.current !== signature) {
            pendingToastSignatureRef.current = signature;
            const pendingNames = pendingMembers.join(', ');
            const remaining = pendingCount > pendingMembers.length
              ? ` and ${pendingCount - pendingMembers.length} more`
              : '';

            toast.warning(`${pendingCount} pool member(s) with pending fees: ${pendingNames}${remaining}`, {
              containerId: "pool-toasts",
              position: "top-right",
              autoClose: 8000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          }
        }
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error("Error fetching pool members:", error);
        setMembers([]);
        setTotalCount(0);
        if (typeof setTodayMembersCount === 'function') {
          setTodayMembersCount(0);
        }
        toast.error("Failed to fetch members data!", {
          containerId: "pool-toasts"
        });
      } finally {
        if (isActive) {
          setHasLoadedMembers(true);
          setIsLoading(false);
        }
      }
    };

    fetchMembers();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [debouncedSearchTerm, feesFilter, page, rowsPerPage, reloadKey, setTodayMembersCount]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (member) => {
    setSelectedMember(member);
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedMember(null);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedMember(null);
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setOpenDeleteModal(true);
  };

  const handleUpdateMember = async (updatedMember) => {
    try {
      await poolMembersService.updateSwimmer(updatedMember);
      setReloadKey((currentValue) => currentValue + 1);
      toast.success('Pool member updated successfully!', {
        containerId: "pool-toasts"
      });
    } catch (error) {
      console.error("Error updating pool member:", error);
      toast.error('Failed to update pool member!', {
        containerId: "pool-toasts"
      });
    }
  };

  const handleDeleteMember = async () => {
    try {
      await poolMembersService.deleteSwimmer(memberToDelete._id);
      if (members.length === 1 && page > 0) {
        setPage(page - 1);
      } else {
        setReloadKey((currentValue) => currentValue + 1);
      }
      toast.success("Pool member deleted successfully!");
    } catch (error) {
      console.error("Error deleting pool member:", error);
      toast.error("Error occurred while deleting pool member");
    } finally {
      setOpenDeleteModal(false);
      setMemberToDelete(null);
    }
  };
  
  const handleNameClick = (member) => {
    console.log("Member clicked:", member); // Add debug log
    setSelectedMember(member);
    const measurementData = {
      Name: member.Name,
      Weight: member.Weight || "",
      Chest: member.Chest || "",
      Stomach: member.Stomach || "",
      LowerTier: member.LowerTier || "",
      Waist: member.Waist || "",
      Arm: member.Arm || "",
      Hip: member.Hip || "",
      Thigh: member.Thigh || "",
    };
    console.log("Setting measurements:", measurementData); // Add debug log
    setSelectedMemberMeasurements(measurementData);
    setMeasurementsModalOpen(true);
  };

  const showInitialLoading = isLoading && !hasLoadedMembers;
  const emptyTableText = isLoading ? 'Searching...' : 'No members found';

  return (
    <>
      <ToastContainer containerId="pool-toasts"/>
      <div className="gym-members-container" aria-busy={isLoading}>
        {showInitialLoading ? (
          <div className="members-initial-loading">
            <CircularProgress size={36} sx={{ color: '#6d2057' }} />
            <Typography variant="body2" color="text.secondary">Loading members...</Typography>
          </div>
        ) : (
          <>
            <div className="members-toolbar">
              <TextField
                className="search-field"
                label="Search members"
                variant="outlined"
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ flexGrow: 1 }}
              />
              <IconButton aria-label="Filter members" onClick={handleFilterClick}>
                <FilterIcon
                  style={{
                    backgroundColor: "#6d2057",
                    color: "white",
                    padding: "10px",
                    fontSize: "24px",
                  }}
                />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => handleFilterClose()}
              >
                <MenuItem onClick={() => handleFilterClose("all")}>
                  All Members
                </MenuItem>
                <MenuItem onClick={() => handleFilterClose("pending")}>
                  Pending Fees
                </MenuItem>
                <MenuItem onClick={() => handleFilterClose("clear")}>
                  Paid Fees
                </MenuItem>
              </Menu>
            </div>

            <TableContainer component={Paper} className="table-container">
              <Table className="members-table">
                <TableHead className="table-head">
                  <TableRow>
                    <TableCell className="table-head-cell">Image</TableCell>
                    <TableCell className="table-head-cell">Name</TableCell>
                    <TableCell className="table-head-cell">Address</TableCell>
                    <TableCell className="table-head-cell">Mobile</TableCell>
                    <TableCell className="table-head-cell">
                      Occupation
                    </TableCell>
                    <TableCell className="table-head-cell">Age</TableCell>
                    <TableCell className="table-head-cell">
                      Start date
                    </TableCell>
                    <TableCell className="table-head-cell">Fees Date</TableCell>
                    <TableCell className="table-head-cell">
                      Fees Status
                    </TableCell>
                    <TableCell className="table-head-cell">
                      Pending Fees
                    </TableCell>
                    <TableCell className="table-head-cell">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" className="empty-table-cell">
                        {emptyTableText}
                      </TableCell>
                    </TableRow>
                  ) : members.map((member) => (
                      <TableRow key={member._id} className="table-row">
                        <TableCell>
                          {member.Image ? (
                            <img
                              src={member.Image}
                              alt="Member"
                              className="member-image"
                            />
                          ) : (
                            <span>No Image</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            onClick={() => handleNameClick(member)}
                            sx={{
                              textTransform: "none",
                              color: "#6d2057",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {member.Name}
                          </Button>
                        </TableCell>
                        <TableCell>{member.Address}</TableCell>
                        <TableCell>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {member.Mobile}
                            <IconButton
                              size="small"
                              href={`tel:${member.Mobile}`}
                              style={{ padding: "4px" }}
                            >
                              <PhoneIcon
                                style={{ fontSize: "20px", color: "#6d2057" }}
                              />
                            </IconButton>
                          </div>
                        </TableCell>

                        <TableCell>{member.Occupation}</TableCell>
                        <TableCell>{member.Age}</TableCell>
                        <TableCell>{member.Startdate}</TableCell>
                        <TableCell>{member.Duedate}</TableCell>
                        <TableCell
                          style={{
                            color:
                              member.FeesStatus === "pending"
                                ? "#ff0000"
                                : member.FeesStatus === "clear"
                                ? "#008000"
                                : "inherit",
                          }}
                        >
                          {member.FeesStatus || "Unknown"}
                        </TableCell>
                        <TableCell>{member.PendingFees || 0}</TableCell>
                        <TableCell>
                          <IconButton
                            aria-label="edit"
                            className="action-icon"
                            onClick={() => handleEditClick(member)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            aria-label="delete"
                            className="action-icon"
                            onClick={() => handleDeleteClick(member)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <EditMemberModal
              open={openEditModal}
              handleClose={handleCloseEditModal}
              member={selectedMember}
              handleUpdate={handleUpdateMember}
            />
            <DeleteConfirmationModal
              open={openDeleteModal}
              handleClose={handleCloseDeleteModal}
              member={selectedMember}
              handleDelete={handleDeleteMember}
            />
            <Modal
              open={measurementsModalOpen}
              onClose={() => setMeasurementsModalOpen(false)}
              aria-labelledby="measurements-modal"
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "80%",
                  maxWidth: 800,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  boxShadow: 24,
                  p: 4,
                }}
              >
                <Typography variant="h5" gutterBottom>
                  {selectedMemberMeasurements?.Name}'s Measurements
                </Typography>
                <MemberMeasurements
                  measurements={selectedMemberMeasurements}
                  memberId={selectedMember?._id}
                  onMeasurementsUpdate={(newMeasurements) => {
                    setSelectedMemberMeasurements({
                      ...selectedMemberMeasurements,
                      ...newMeasurements,
                    });
                  }}
                />
                <Button
                  onClick={() => setMeasurementsModalOpen(false)}
                  sx={{ mt: 2 }}
                  variant="contained"
                >
                  Close
                </Button>
              </Box>
            </Modal>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[200, 300, 400]}
            />
          </>
        )}
      </div>
    </>
  );
};

export default PoolMembers;
